import { useEffect, useState } from 'react';
import { clientPortalApi, MUNICIPAL_BANDS } from '../api';
import { findAnalysisLabel } from '../analyses';
import type { LngLat } from '../routeGeo';

export type LoadState = 'loading' | 'error' | 'ready';

export interface LineContext {
  label: string;
  routeMode: 'CIRCUIT' | null;
  idaKm: number;
  vueltaKm: number;
  idaTimeMin: number | null;
  vueltaTimeMin: number | null;
}

interface RouteShapesBundle {
  routeShapesTechnical: { IDA: { coordinates: LngLat[] }; VUELTA: { coordinates: LngLat[] } };
  zones: { IDA: unknown[]; VUELTA: unknown[] };
  totalM: { IDA: number; VUELTA: number };
}

/**
 * Único fetch de `getAnalysisBundle`+`getAnalysisSummary` para toda
 * LineOperationsPage — geometría IDA/VUELTA para Recorrido/Velocidades/
 * Cobertura, y `zones`/`totalM` (mismas claves que ya usa el visor legacy
 * para el gradiente de velocidad) para la pestaña Velocidades. Un solo
 * lugar, ningún tab vuelve a pedir el bundle por separado.
 */
export function useLineGeometry(
  slug: string,
  token: string | null,
  analysisId: string
): {
  ida: LngLat[];
  vuelta: LngLat[];
  zonesIda: unknown[];
  zonesVuelta: unknown[];
  totalMIda: number;
  totalMVuelta: number;
  context: LineContext | null;
  loadState: LoadState;
} {
  const [ida, setIda] = useState<LngLat[]>([]);
  const [vuelta, setVuelta] = useState<LngLat[]>([]);
  const [zonesIda, setZonesIda] = useState<unknown[]>([]);
  const [zonesVuelta, setZonesVuelta] = useState<unknown[]>([]);
  const [totalMIda, setTotalMIda] = useState(0);
  const [totalMVuelta, setTotalMVuelta] = useState(0);
  const [context, setContext] = useState<LineContext | null>(null);
  const [loadState, setLoadState] = useState<LoadState>('loading');

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setLoadState('loading');

    Promise.all([
      clientPortalApi.getAnalysisBundle(slug, analysisId, token),
      // Un solo band alcanza: routeMode/longitud no varían por banda horaria.
      clientPortalApi.getAnalysisSummary(slug, token, 'Hábil', MUNICIPAL_BANDS[0]),
    ])
      .then(([bundleRaw, summary]) => {
        if (cancelled) return;
        const row =
          summary.lines.find(l => l.analysisId === analysisId) ??
          summary.lines.flatMap(l => l.variants).find(v => v.analysisId === analysisId) ??
          null;
        if (!row) {
          setLoadState('error');
          return;
        }
        const bundle = bundleRaw as RouteShapesBundle;
        setIda(bundle.routeShapesTechnical.IDA.coordinates);
        setVuelta(bundle.routeShapesTechnical.VUELTA.coordinates);
        setZonesIda(bundle.zones?.IDA ?? []);
        setZonesVuelta(bundle.zones?.VUELTA ?? []);
        setTotalMIda(bundle.totalM?.IDA ?? 0);
        setTotalMVuelta(bundle.totalM?.VUELTA ?? 0);
        setContext({
          label: row.label ?? findAnalysisLabel(analysisId),
          routeMode: row.routeMode,
          idaKm: row.idaKm,
          vueltaKm: row.vueltaKm,
          idaTimeMin: row.idaTimeMin,
          vueltaTimeMin: row.vueltaTimeMin,
        });
        setLoadState('ready');
      })
      .catch(() => {
        if (!cancelled) setLoadState('error');
      });

    return (): void => {
      cancelled = true;
    };
  }, [slug, token, analysisId]);

  return { ida, vuelta, zonesIda, zonesVuelta, totalMIda, totalMVuelta, context, loadState };
}
